import * as Sentry from '@sentry/react';
import { FirebaseError } from '@firebase/util';
import {
  doc,
  collection,
  getDoc,
  getDocs,
  getAggregateFromServer,
  sum,
  query,
  where,
  orderBy,
  onSnapshot,
  Timestamp,
  type DocumentData,
  type DocumentReference,
  type DocumentSnapshot,
  type Query,
  type QuerySnapshot,
} from 'firebase/firestore';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);
import { db } from '../../config';
import {
  companyStatus,
  companyPlan,
  userRole,
  registerStatus,
  paymentCycle as paymentCycleEnum,
  patternStatus,
  masterStatus,
  planLimits as enumPlanLimits,
} from '../../../../constants/enum';
import { getPlanPricing, getPlanLimits } from '../../../../constants/plans';
import type { CompanyAccountType, CompanyDetailType } from '../../../../types/company';
import type { PaymentCycle } from '../../../../types/plan';

export const companiesRef = collection(db, 'companies');

const companyDocument = (companyId: string): DocumentReference<DocumentData> =>
  doc(companiesRef, companyId);

const companySnapshot = async (companyId: string): Promise<DocumentSnapshot> =>
  await getDoc(companyDocument(companyId));

/**
 * 企業情報を取得
 */
export const getCompany = async (companyId: string): Promise<DocumentData | undefined> => {
  try {
    const company = await companySnapshot(companyId);
    const companyData = company.data();
    return companyData;
  } catch (error) {
    return undefined;
  }
};

/**
 * 全企業を取得（削除済みを除く）
 */
export const getAllCompanies = async (): Promise<DocumentData[]> => {
  const companiesQuery: Query<DocumentData> = query(
    companiesRef,
    where('status', '!=', companyStatus.deleted),
    orderBy('status', 'asc'),
    orderBy('created_at', 'desc'),
  );
  const querySnapshot: QuerySnapshot<DocumentData> = await getDocs(companiesQuery);
  return querySnapshot.docs.map(doc => ({
    ...doc.data(),
    company_id: doc.id,
  }));
};

/**
 * 有効な企業のみ取得
 */
export const getActiveCompanies = async (): Promise<DocumentData[]> => {
  const companiesQuery: Query<DocumentData> = query(
    companiesRef,
    where('status', '==', companyStatus.active),
    orderBy('created_at', 'desc'),
  );
  const querySnapshot: QuerySnapshot<DocumentData> = await getDocs(companiesQuery);
  return querySnapshot.docs.map(doc => ({
    ...doc.data(),
    company_id: doc.id,
  }));
};

/**
 * アカウント管理画面用の企業情報を取得
 */
export const getCompanyForAccount = async (
  companyId: string,
): Promise<CompanyAccountType | null> => {
  try {
    const company = await companySnapshot(companyId);
    if (!company.exists()) {
      return null;
    }

    const data = company.data();
    const planCode = data.plan ?? 0;
    const planLimits = getPlanLimits(planCode);

    return {
      company_id: company.id,
      company_name: data.company_name,
      phone_number: data.phone_number,
      plan: planCode,
      payment_cycle: data.payment_cycle ?? 0,
      user_limit: data.user_limit ?? planLimits?.userCount ?? 0,
      current_user_count: data.current_user_count ?? 0,
      pattern_limit:
        data.pattern_limit ??
        (planCode === companyPlan.free
          ? enumPlanLimits.free.patternLimit
          : enumPlanLimits.standard.patternLimit),
      // 未設定（本機能より前に作られた企業）は制限なし扱いのため既定値で埋めない（#3288）
      master_record_limit: data.master_record_limit,
      ocr_monthly_budget: data.ocr_monthly_budget ?? planLimits?.ocrMonthlyBudget ?? 0,
      current_month_ocr_count: data.current_month_ocr_count ?? 0,
      current_month_ocr_cost: data.current_month_ocr_cost ?? 0,
      ocr_credit_balance: data.ocr_credit_balance ?? 0,
      contract_start_date: data.contract_start_date?.toDate(),
      contract_end_date: data.contract_end_date?.toDate(),
      allow_ai_learning: data.allow_ai_learning ?? true,
      enable_master: data.enable_master ?? false,
      master_self_upload: data.master_self_upload ?? false,
      enable_dictionary: data.enable_dictionary ?? false,
    };
  } catch (error) {
    return null;
  }
};

/**
 * 運営_企業詳細画面用の企業情報を取得
 */
export const getCompanyDetail = async (companyId: string): Promise<CompanyDetailType | null> => {
  const company = await companySnapshot(companyId);
  if (!company.exists()) {
    return null;
  }

  const data = company.data();

  // 担当代表者（企業管理者）を取得（最初に登録されたユーザーを代表者とする）
  const usersRef = collection(db, 'users');
  const representativeQuery: Query<DocumentData> = query(
    usersRef,
    where('company_id', '==', companyId),
    where('role', '==', userRole.companyAdmin),
    where('register_status', '!=', registerStatus.deleted),
    orderBy('register_status', 'asc'),
    orderBy('created_at', 'asc'),
  );
  const representativeSnapshot: QuerySnapshot<DocumentData> = await getDocs(representativeQuery);
  const representative = representativeSnapshot.docs[0]?.data();

  // プランマスタから料金・制限値を取得
  const planCode = data.plan ?? 0;
  // payment_cycle を数値から文字列に変換
  const paymentCycleValue = data.payment_cycle as number | undefined;
  const paymentCycle: PaymentCycle | undefined =
    paymentCycleValue === paymentCycleEnum.monthly
      ? 'monthly'
      : paymentCycleValue === paymentCycleEnum.yearly
        ? 'yearly'
        : paymentCycleValue === paymentCycleEnum.none
          ? 'none'
          : undefined;
  const planPricing =
    getPlanPricing(planCode, paymentCycle ?? 'none') ??
    getPlanPricing(planCode, 'monthly') ??
    getPlanPricing(planCode, 'yearly');
  const monthlyFee = planPricing?.monthlyPrice ?? 0;
  const planLimits = getPlanLimits(planCode);

  // 今月の代理操作回数を取得（単一whereでインデックス不要、日付はJS側でフィルタ）
  const now = new Date();
  const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const ocrUsageRef = collection(db, 'companies', companyId, 'ocr_usage_monthly');
  const proxyUsageQuery = query(ocrUsageRef, where('is_proxy', '==', true));
  const proxyDocs = await getDocs(proxyUsageQuery);
  let currentMonthProxyCount = 0;
  proxyDocs.forEach(d => {
    const executedAt = d.data().executed_at?.toDate?.();
    if (executedAt && executedAt >= firstDayOfMonth) {
      currentMonthProxyCount += (d.data().usage_count as number) ?? 0;
    }
  });

  // 読取パターン数をリアルタイム集計
  const patternsRef = collection(db, 'patterns');
  const patternQuery = query(
    patternsRef,
    where('company_id', '==', companyId),
    where('status', '==', patternStatus.valid),
  );
  const patternSnapshot = await getDocs(patternQuery);
  const currentPatternCount = patternSnapshot.size;

  // AIマスタ照合のレコード数をリアルタイム集計（#3288）。
  // 論理削除済み（status=99）は除外する。status 未設定は有効扱いのため、
  // where で絞らず取得後に JS でフィルタする（masters/read.ts と同じ方針）。
  const mastersRef = collection(db, 'masters');
  const masterQuery = query(mastersRef, where('company_id', '==', companyId));
  const masterSnapshot = await getDocs(masterQuery);
  const currentMasterRecordCount = masterSnapshot.docs
    .map(masterDoc => masterDoc.data())
    .filter(masterData => masterData.status !== masterStatus.deleted)
    .reduce((total, masterData) => total + ((masterData.row_count as number) ?? 0), 0);

  // 月平均利用枚数を計算（総利用枚数 ÷ 経過月数）
  const totalAggregateSnapshot = await getAggregateFromServer(ocrUsageRef, {
    totalCount: sum('usage_count'),
  });
  const totalUsageCount = totalAggregateSnapshot.data().totalCount || 0;
  const createdAt = data.created_at?.toDate() ?? now;
  const monthsDiff =
    (now.getFullYear() - createdAt.getFullYear()) * 12 +
    (now.getMonth() - createdAt.getMonth()) +
    1;
  const monthlyAverageOcrCount = Math.round(totalUsageCount / Math.max(1, monthsDiff));

  // 次回更新日を計算（契約終了日の翌日）
  // 例: 契約期間 2025/11/20〜2026/11/19 の場合、次回更新日は 2026/11/20
  const contractEndDate = data.contract_end_date?.toDate() ?? null;
  const nextRenewalDate = contractEndDate
    ? new Date(contractEndDate.getTime() + 24 * 60 * 60 * 1000)
    : null;

  return {
    company_id: company.id,
    company_name: data.company_name ?? '',
    plan: data.plan ?? 0,
    payment_cycle: paymentCycle ?? null,
    monthly_fee: monthlyFee,
    contract_start_date: data.contract_start_date?.toDate() ?? null,
    next_renewal_date: nextRenewalDate,
    status: data.status ?? 0,
    current_month_ocr_count: data.current_month_ocr_count ?? 0,
    current_month_ocr_cost: data.current_month_ocr_cost ?? 0,
    ocr_monthly_budget: data.ocr_monthly_budget ?? planLimits?.ocrMonthlyBudget ?? 0,
    proxy_operation_count: currentMonthProxyCount,
    ocr_credit_balance: data.ocr_credit_balance ?? 0,
    representative_name: representative?.name ?? '',
    representative_email: representative?.email ?? '',
    phone_number: (data.phone_number as string) ?? '',
    created_at: data.created_at?.toDate() ?? new Date(),
    updated_at: data.updated_at?.toDate() ?? null,
    current_user_count: data.current_user_count ?? 0,
    user_limit: data.user_limit ?? planLimits?.userCount ?? 0,
    current_pattern_count: currentPatternCount,
    pattern_limit:
      data.pattern_limit ??
      (planCode === companyPlan.free
        ? enumPlanLimits.free.patternLimit
        : enumPlanLimits.standard.patternLimit),
    current_master_record_count: currentMasterRecordCount,
    // 未設定（本機能より前に作られた企業）は制限なし扱いのため既定値で埋めない（#3288）
    master_record_limit: data.master_record_limit as number | undefined,
    monthly_average_ocr_count: monthlyAverageOcrCount,
    crm_id: (data.crm_id as string) ?? '',
    payment_method: (data.payment_method as number) ?? 0,
    stripe_customer_id: (data.stripe_customer_id as string) ?? '',
    stripe_subscription_id: (data.stripe_subscription_id as string) ?? '',
    enable_master: (data.enable_master as boolean) ?? false,
    master_self_upload: (data.master_self_upload as boolean) ?? false,
    enable_dictionary: (data.enable_dictionary as boolean) ?? false,
    enable_export_template: (data.enable_export_template as boolean) ?? false,
    enable_custom_functions: (data.enable_custom_functions as boolean) ?? false,
    webhook_enabled: data.webhook_enabled === true,
    api_enabled: data.api_enabled === true,
    has_security_addon: data.has_security_addon === true,
    email_import_enabled: data.email_import_enabled === true,
  };
};

/**
 * 企業ごとの総利用枚数を取得（ocr_usage_monthly から累計、代理操作分を除外）
 * @param companyId - 企業ID
 * @returns 企業全体の累計利用枚数（is_proxy: true を除外）。取得失敗時は 0 を返す
 */
export const getTotalUsageCountByCompanyId = async (companyId: string): Promise<number> => {
  try {
    const ocrUsageRef = collection(db, 'companies', companyId, 'ocr_usage_monthly');

    // 全体の利用枚数と代理操作分を並行取得
    const proxyQuery = query(ocrUsageRef, where('is_proxy', '==', true));
    const [totalSnapshot, proxySnapshot] = await Promise.all([
      getAggregateFromServer(ocrUsageRef, { totalCount: sum('usage_count') }),
      getAggregateFromServer(proxyQuery, { proxyCount: sum('usage_count') }),
    ]);

    const totalCount = totalSnapshot.data().totalCount || 0;
    const proxyCount = proxySnapshot.data().proxyCount || 0;

    return totalCount - proxyCount;
  } catch {
    return 0;
  }
};

/**
 * 指定ユーザーの当月の実際の消費枚数を一度だけ取得（#2804 Phase4）
 * ocr_usage_monthly サブコレクションの usage_count を合計（代理操作は除外）
 * 実行前の月間上限チェック用。当月（JST月初〜）の executed_at 範囲で集計するため自然リセット
 * @param companyId - 企業ID
 * @param userId - ユーザーID
 * @returns 当月の消費ページ数
 */
export const getMonthlyUsageCountByUserId = async (
  companyId: string,
  userId: string,
): Promise<number> => {
  const timeZone = 'Asia/Tokyo';
  const startOfMonth = dayjs().tz(timeZone).startOf('month');
  const firstDayTimestamp = Timestamp.fromDate(startOfMonth.toDate());

  const ocrUsageRef = collection(db, 'companies', companyId, 'ocr_usage_monthly');
  const usageQuery = query(
    ocrUsageRef,
    where('user_id', '==', userId),
    where('is_proxy', '==', false),
    where('executed_at', '>=', firstDayTimestamp),
  );

  const snapshot = await getDocs(usageQuery);
  let totalCount = 0;
  snapshot.docs.forEach(d => {
    totalCount += (d.data().usage_count as number) ?? 0;
  });
  return totalCount;
};

/**
 * 指定ユーザーの当月の実際の消費枚数をリアルタイム監視で取得
 * ocr_usage_monthly サブコレクションの usage_count を合計
 * @param companyId - 企業ID
 * @param userId - ユーザーID
 * @param callback - 枚数更新時のコールバック
 * @returns リスナー解除関数
 */
export const getMonthlyUsageCountByUserIdRealTime = (
  companyId: string,
  userId: string,
  callback: (count: number) => void,
): (() => void) => {
  const timeZone = 'Asia/Tokyo';
  const now = dayjs().tz(timeZone);
  const startOfMonth = now.startOf('month');
  const firstDayTimestamp = Timestamp.fromDate(startOfMonth.toDate());

  const ocrUsageRef = collection(db, 'companies', companyId, 'ocr_usage_monthly');
  const usageQuery = query(
    ocrUsageRef,
    where('user_id', '==', userId),
    where('is_proxy', '==', false),
    where('executed_at', '>=', firstDayTimestamp),
  );

  const unsubscribe = onSnapshot(
    usageQuery,
    querySnapshot => {
      let totalCount = 0;
      querySnapshot.docs.forEach(d => {
        const usageCount = (d.data().usage_count as number) ?? 0;
        totalCount += usageCount;
      });
      callback(totalCount);
    },
    error => {
      // ログアウト直後にリスナーが permission-denied を受け取るのは正常な動作
      if (error instanceof FirebaseError && error.code === 'permission-denied') {
        return;
      }
      Sentry.captureException(error);
    },
  );

  return unsubscribe;
};
